<template>
	<div ref="root" class="ml-container" v-if="isMapAllowed" :class="offlineZoneSelecting ? 'selecting' : ''">
		<div class="tool-box-container" v-if="(toolBoxTools && toolBoxTools.length > 0) || (legendItems && legendItems.length > 0)">
			<div v-for="tool in toolBoxTools.filter((t) => (toolBox && toolBox.includes(t.id)) || t.isDefault)" :key="tool.id" :class="'tool-box-item' + (tool.active && !tool.oneShot ? ' active' : '') + (tool.hidden() ? ' hidden' : '') + (tool.isMapControl ? ' map-control' : '') + (tool.className ? ' ' + tool.className : '')">
				<v-tooltip :attach="overlayTarget" location="start">
					<template v-slot:activator="{ props }">
						<span v-bind="props" @click="tool.action">
							<v-icon>{{ typeof tool.icon === 'function' ? tool.icon() : tool.icon }}</v-icon>
						</span>
					</template>
					<span>{{ typeof tool.tooltip === 'function' ? tool.tooltip() : tool.tooltip }}</span>
				</v-tooltip>
			</div>

			<div :class="toolBoxTools && toolBoxTools.length > 0 ? 'legend-container tool-box-item with-separator' : 'legend-container tool-box-item'" v-if="legendItems && legendItems.length > 0">
				<v-tooltip :attach="overlayTarget" location="start">
					<template v-slot:activator="{ props }">
						<span v-bind="props" @click="toggleLegend">
							<v-icon color="primary">info</v-icon>
						</span>
					</template>
					<span>{{ t('Legend') }}</span>
				</v-tooltip>
				<div :class="['legend-panel', { visible: legendVisible }]">
					<div class="legend-header">
						<h4>{{ t('Legend') }}</h4>
						<md-icon-button @click="toggleLegend">
							<v-icon>close</v-icon>
						</md-icon-button>
					</div>
					<div class="legend-content">
						<div v-for="type in legendItems" :key="type.id" class="legend-item">
							<div class="legend-color" :style="{ backgroundColor: type.color }"></div>
							<span class="legend-label">{{ type.name }}</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<DialogControl v-model="offlinePanelVisible" max-width="340">
			<v-card>
				<v-card-title>
					<v-icon>wifi</v-icon>
					{{ t('OfflineMode') }}
				</v-card-title>
				<v-card-text>
					<p>{{ t('OfflineMapZoneHint') }}</p>
					<v-btn color="primary" variant="tonal" rounded="xl" @click="startZoneSelection">{{ t('SelectZone') }}</v-btn>
					<p v-if="formattedTilesSize !== null">{{ t('DownloadedSize') }} {{ formattedTilesSize }}</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn variant="text" @click="offlinePanelVisible = false">{{ t('Close') }}</v-btn>
				</v-card-actions>
			</v-card>
		</DialogControl>

		<transition name="pop" tag="div">
			<div v-if="offlineZoneSelecting" class="offline-zone-bar">
				<v-btn color="primary" variant="tonal" rounded="xl" @click="confirmZoneSelection">{{ t('ConfirmZone') }}</v-btn>
				<v-btn color="danger" variant="tonal" rounded="xl" @click="cancelZoneSelection">{{ t('Cancel') }}</v-btn>
			</div>
		</transition>

		<div class="bottom-action-bar" v-if="bottomActionBarItems.length > 0">
			<div v-for="tool in bottomActionBarItems" :key="tool.id" :class="['bottom-action-item', { hidden: tool.hidden() }]" @click="tool.action">
				<v-icon>{{ typeof tool.icon === 'function' ? tool.icon() : tool.icon }}</v-icon>
				<span>{{ typeof tool.tooltip === 'function' ? tool.tooltip() : tool.tooltip }}</span>
			</div>
		</div>

		<aside class="map-aside">
			<div class="search-container">
				<div class="search-content">
					<md-outlined-text-field ref="mapSearchTextField" v-model="searchQuery" :label="t('search')" @click="onSearchClick" @input="onSearchInput" @keydown="onSearchKeyDown">
						<v-icon slot="leading-icon">search</v-icon>
						<md-icon-button slot="trailing-icon" @click="clearSearch" v-show="searchQuery.length > 0">
							<v-icon>close</v-icon>
						</md-icon-button>
					</md-outlined-text-field>

					<div class="search-actions">
						<div class="action-container">
							<DeliveryLocationFiltersDialog v-if="needFilter" ref="filtersComponentRef" :needAnchor="true" :locations="locations" @filters-changed="locationFilterChanged" @focus="emit('action-focus')" />
						</div>
						<div class="action-container">
							<DeliveryLocationForm @focus="emit('action-focus')" />
						</div>

						<div class="route-summary" v-if="currentRoute && !hideRouteSummary">
							<div class="route-summary-item">
								<v-icon>schedule</v-icon>
								<span>{{ translateDuration(currentRoute.durationText) }}</span>
							</div>
							<div class="route-summary-item">
								<v-icon v-if="!isMobile">straighten</v-icon>
								<span>{{ translateDistance(currentRoute.distanceText) }}</span>
							</div>
						</div>
					</div>
				</div>

				<div v-if="searchMenuOpen && searchResults.length > 0" class="search-results-menu">
					<div v-for="(result, index) in searchResults" :key="index" :class="['search-result-item', { selected: index === selectedSearchIndex }]" @click="selectSearchResult(result)" @mouseenter="selectedSearchIndex = index">{{ result.name }}</div>
				</div>

				<div v-if="searchMenuOpen && searchResults.length === 0 && searchQuery.length > 0" class="search-results-menu">
					<div class="search-result-item no-results">{{ t('NoResultsFound') }}</div>
				</div>
			</div>

			<div class="marker-detail-container" :class="markerDetailClasses" ref="markerDetailRef" v-if="detailMarkerVisible">
				<div>
					<div class="marker-detail-header" ref="markerDetailHeaderRef" @click="onMarkerDetailHeaderClick">
						<div class="drag-bar" v-if="isTablet"></div>
						<div class="marker-detail-title">
							<img v-if="detailMarkerData.accountLogo" :src="detailMarkerData.accountLogo" class="account-logo" :alt="detailMarkerData.accountName || ''" />
							<span v-if="detailMarkerData.sps" class="delivery-location-sps">{{ detailMarkerData.sps }}</span>
							<v-icon v-if="detailMarkerData.isShared" class="delivery-location-shared" size="small">share</v-icon>
							<span>{{ detailMarkerData.title }}</span>
						</div>
						<md-icon-button class="marker-detail-close-button" @click="hideDetailMarker">
							<v-icon @click="hideDetailMarker">close</v-icon>
						</md-icon-button>
					</div>

					<div class="marker-detail-actions" v-if="detailMarkerVisible">
						<md-icon-button class="action-button edit-button" @click="editDeliveryLocation"><v-icon>edit</v-icon></md-icon-button>
						<md-icon-button class="action-button phone-button" @click="callPhoneNumber" :disabled="!detailMarkerData.phoneNumber"><v-icon>call</v-icon></md-icon-button>
						<md-icon-button class="action-button calendar-button" @click="addNewVisit"><v-icon>calendar_add_on</v-icon></md-icon-button>
						<md-icon-button class="action-button directions-button" @click="showDirections"><v-icon>directions</v-icon></md-icon-button>
						<md-icon-button class="action-button maps-button" @click="openInMapsApplication"><v-icon>navigation</v-icon></md-icon-button>
					</div>
				</div>

				<div class="marker-detail-content">
					<slot name="marker-detail" :data="detailMarkerData">
						<div v-if="detailMarkerData.kpis && detailMarkerData.kpis.length > 0 && !detailMarkerLoading" class="kpi-circles-container">
							<div class="kpi-circles-container">
								<div v-for="kpi in detailMarkerData.kpis" :key="kpi.name" class="kpi-circle-item">
									<div class="kpi-circle-wrapper">
										<v-progress-circular :model-value="kpi.percentage" :color="kpi.color" :size="80" :width="8">
											<span class="kpi-value">{{ kpi.value }}%</span>
										</v-progress-circular>
									</div>
									<div class="kpi-info">
										<div class="kpi-name">{{ t(kpi.name) }}</div>
										<div class="kpi-objective">{{ kpi.infos }}</div>
									</div>
								</div>
							</div>
						</div>

						<div v-for="[key, value] in Object.entries(detailMarkerData.fields || {})" :key="key">
							<md-outlined-text-field :label="key" :value="value" readonly></md-outlined-text-field>
						</div>

						<div v-if="detailMarkerLoading" class="marker-detail-skeleton-container">
							<div class="marker-fields-skeleton">
								<div v-for="index in 6" :key="`field-skeleton-${index}`" class="marker-field-skeleton">
									<v-skeleton-loader type="text"></v-skeleton-loader>
								</div>
							</div>

							<div class="kpi-skeleton-container">
								<div v-for="index in 2" :key="`kpi-skeleton-${index}`" class="kpi-skeleton-item">
									<v-skeleton-loader type="avatar" class="kpi-skeleton-circle"></v-skeleton-loader>
									<v-skeleton-loader type="text" class="kpi-skeleton-title"></v-skeleton-loader>
									<v-skeleton-loader type="text" class="kpi-skeleton-subtitle"></v-skeleton-loader>
								</div>
							</div>

							<div class="comment-skeleton">
								<v-skeleton-loader type="text@3"></v-skeleton-loader>
							</div>
						</div>

						<div v-if="detailMarkerData.lastVisitComment && !detailMarkerLoading" class="last-visit-comment">
							<md-outlined-text-field :label="t('LastVisitComment')" :value="detailMarkerData.lastVisitComment" readonly type="textarea" rows="3"></md-outlined-text-field>
						</div>
					</slot>
				</div>
			</div>
		</aside>
	</div>
	<div v-else class="offline-message">
		<v-icon>wifi_off</v-icon>
		<span>{{ t('MapNotAvailableOffline') }}</span>
	</div>

	<v-snackbar v-model="searchingPosition" :timeout="-1" :loading="true" rounded="xl" color="info">
		{{ t('SearchingUserPosition') }}
	</v-snackbar>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick, inject, computed } from 'vue';
import { dragBottomSheetHandler } from '@/utils/ui/dragBottomSheetHandler';
import 'maplibre-gl/dist/maplibre-gl.css';
import { getAccountImageUrl } from '@/api/core/file';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { isSharedDeliveryLocationId } from '@/utils/domain/sharedDeliveryLocations';
import { useTranslator } from '@/composables/useTranslator';
import { getDeliveryLocationTypeIcon, getDeliveryLocationTypeColor } from '@/utils/domain/appointmentUtils';
import { routeService } from '@/services/routeService';
import { OfflineMapManager, OfflineManagerControl } from 'map-gl-offline';
import { deviceUtils } from '@/utils/core/deviceUtils';
import DeliveryLocationFiltersDialog from '@/components/dialogs/DeliveryLocationFiltersDialog.vue';
import DeliveryLocationForm from '@/components/forms/DeliveryLocationForm.vue';
import DialogControl from '@/components/controls/DialogControl.vue';

const emit = defineEmits(['marker-click', 'bounds-changed', 'action-focus', 'edit-delivery-location', 'add-new-visit', 'phone-number-copied', 'marker-detail-hidden', 'filters-changed', 'toolbox-action', 'marker-drag-drop', 'marker-drag-start', 'marker-drag-cancel', 'route-summary-changed']);
defineExpose({
	fitBounds,
	setCenterZoom,
	setFilter,
	setLines,
	setPolys,
	setHeat,
	resize,
	fitAll,
	resizeDetailMarkerContent,
	setLocations,
	addLocations,
	clearLocations,
	focusLocation,
	selectLocation,
	removeCurrentPositionMarker,
	ensureSourcesAndLayers,
	showDetailMarker,
	hideDetailMarker,
	navigateToLocation,
	clearRoutes,
	drawRouteAlongWaypoints,
	addCurrentPositionMarker,
	getRouteSummary,
	getToolState,
	clearHtmlMarkers,
	applyFilters,
	isFilterDirty,
	getUserPosition,
	isGeolocationActive,
	refreshUserPosition,
	fitAllWithUserPosition,
	fitCoordinates,
	reinitMap,
	destroyMap,
});

const props = defineProps({
	points: { type: Array, default: () => [] },
	cluster: { type: Boolean, default: false },
	initialCenter: { type: Array, default: () => [2.3514, 48.8575] },
	initialZoom: { type: Number, default: 10 },
	interactive: { type: Boolean, default: true },
	minZoom: { type: Number, default: 2 },
	maxZoom: { type: Number, default: 18 },
	showPopupOnClick: { type: Boolean, default: true },
	fullscreen: { type: Boolean, default: false },
	hideRouteSummary: { type: Boolean, default: false },
	polylines: { type: Array, default: () => [] },
	polygons: { type: Array, default: () => [] },
	heatmap: { type: Array, default: () => [] },
	popupTemplate: { type: Function, default: null },
	hoverHighlight: { type: Boolean, default: true },
	clusterRadius: { type: Number, default: 40 },
	clusterMaxZoom: { type: Number, default: 12 },

	detailMarkerRenderer: { type: Function, default: null },
	locations: { type: Array, default: () => [] },
	allLocations: { type: Array, default: () => [] },
	locationIdKey: { type: String, default: 'id' },
	locationTitleKey: { type: String, default: 'label' },
	routeGeoJson: { type: Object, default: null },
	selectedDate: { type: Date, default: null },

	searchIndexKeys: { type: Array, default: () => [] },
	filterIndexKeys: { type: Array, default: () => [] },
	legend: { type: Array, default: () => [] },
	toolBox: { type: Array, default: () => [] },
	bottomToolBox: { type: Array, default: () => [] },
	needFilter: { type: Boolean, default: false },
	hideDetailOnMini: { type: Boolean, default: false },
	enableMarkerDrag: { type: Boolean, default: false },
	autoFit: { type: Boolean, default: true },
	// When false, the underlying map instance is NOT created on mount — the owner must call
	// reinitMap() explicitly. Used by CalendarMap so the agenda map only spins up in day view.
	autoInit: { type: Boolean, default: true },
});

const CLUSTER_AUTO_THRESHOLD = 500;
const clusterEnabled = computed(() => {
	if (props.cluster) return true;
	const count = props.points?.length || props.locations?.length || 0;
	return count > CLUSTER_AUTO_THRESHOLD;
});

const root = ref(null);
const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const toast = iframeDataBridge.getIframeWindow().toast;
const user = iframeDataBridge.getCurrentUser();
const salesHelper = iframeDataBridge.getSalesHelper();
const isTouchMode = ref(deviceUtils.isTouchMode());
const isTablet = ref(deviceUtils.isTablet());
const isMobile = ref(deviceUtils.isMobile());
const onResize = () => {
	if (iframeMap) {
		iframeMap.resize();
	}

	isTouchMode.value = deviceUtils.isTouchMode();
	isTablet.value = deviceUtils.isTablet();
	isMobile.value = deviceUtils.isMobile();
};
const isMapAllowed = ref(salesHelper.isGMAPActivated() && iframeDataBridge.isOnline());

let containerResizeObserver = null;
function observeContainerResize() {
	if (containerResizeObserver || !root.value || typeof ResizeObserver === 'undefined') return;
	containerResizeObserver = new ResizeObserver(() => {
		if (iframeMap) iframeMap.resize();
	});
	containerResizeObserver.observe(root.value);
}
function disconnectContainerResize() {
	if (!containerResizeObserver) return;
	containerResizeObserver.disconnect();
	containerResizeObserver = null;
}

let iframeDocument = null;
let iframeMap = null;
let iframeContainer = null;
let iframeLib = null;
let offlineManager = null;
let offlineControl = null;
let styleUrl = null;

let isLoaded = false;
let markerClickInProgress = false;
let pendingRouteWaypoints = null;
let clusterModeActive = false;
let isMarkerDragActive = false;
let markerRenderToken = 0;
let dragGhostWatchdogDoc = null;

const detailMarkerVisible = ref(false);
const detailMarkerHiding = ref(false);
const detailMarkerLoading = ref(false);
const detailMarkerData = ref({
	title: '',
	sps: null,
	isShared: false,
	accountLogo: null,
	accountName: '',
	fields: {},
	kpis: [],
});

const dragSenderMode = ref('mid');
const markerDetailRef = ref(null);
const markerDetailHeaderRef = ref(null);
const dragSenderIsDragging = ref(false);
const markerDetailDrag = ref(null);

const markerDetailClasses = computed(() => {
	const cls = {
		visible: detailMarkerVisible.value && !detailMarkerHiding.value,
		hiding: detailMarkerHiding.value,
		loading: detailMarkerLoading.value,
		'touch-mode': isTablet.value,
	};
	if (isTablet.value) cls[dragSenderMode.value] = true;
	return cls;
});

const userCurrentPosition = ref(null);
const geolocationActive = ref(false);
const searchingPosition = ref(false);
const deliveryLocationsGeo = ref([]);
const originalLocations = ref([]);

const searchQuery = ref('');
const searchMenuOpen = ref(false);
const searchResults = ref([]);
const searchIndex = ref([]);
const selectedSearchIndex = ref(0);
const selectedMarkerId = ref(null);

const legendVisible = ref(false);
const legendItems = computed(() => {
	if (props.legend?.length) return props.legend;

	const deliveryLocationTypes = iframeDataBridge.getEcosVM()?.deliveryLocationTypes;
	if (!deliveryLocationTypes?.length) return [];

	const usedTypeIds = new Set(props.locations.map((loc) => loc.deliveryLocationTypeId).filter(Boolean));
	if (!usedTypeIds.size) return [];

	return deliveryLocationTypes
		.filter((type) => usedTypeIds.has(type.id))
		.map((type) => ({
			id: type.id,
			name: type.name || type.reference,
			color: getDeliveryLocationTypeColor({ deliveryLocationTypeId: type.id }),
			system: type.system,
		}))
		.sort((a, b) => {
			if (a.system && !b.system) return -1;
			if (!a.system && b.system) return 1;
			return a.name.localeCompare(b.name);
		});
});
const currentRoute = ref(null);

const offlinePanelVisible = ref(false);
const offlineZoneSelecting = ref(false);
const offlineZoneBounds = ref(null);
const offlineTilesSize = ref(null);

const formattedTilesSize = computed(() => {
	if (offlineTilesSize.value === null) return null;
	const bytes = offlineTilesSize.value;
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
});

const toolBoxTools = ref([
	{
		id: 'allDeliveryLocations',
		icon: 'storefront',
		tooltip: t('AllDeliveryLocations'),
		active: false,
		hidden: () => false,
		action: () => {
			const tool = toolBoxTools.value.find((t) => t.id === 'allDeliveryLocations');
			tool.active = !tool.active;

			if (tool.active) {
				tool.tooltip = t('RemoveAllDeliveryLocations');
			} else {
				tool.tooltip = t('AllDeliveryLocations');
			}

			emit('toolbox-action', { toolId: 'allDeliveryLocations', active: tool.active });
		},
	},
	{
		id: 'offline',
		icon: 'wifi',
		tooltip: t('OfflineMode'),
		active: false,
		hidden: () => false,
		action: () => {
			offlinePanelVisible.value = !offlinePanelVisible.value;
		},
	},
	{
		id: 'travelMode',
		isDefault: true,
		icon: ref(() => (routeService.getTravelMode() === 'car' ? 'directions_car' : 'directions_walk')),
		tooltip: ref(() => (routeService.getTravelMode() === 'car' ? t('ByCar') : t('ByWalking'))),
		active: false,
		oneShot: true,
		hidden: () => routeService.isGoogleAllowed() === false,
		action: () => {
			const tool = toolBoxTools.value.find((t) => t.id === 'travelMode');
			tool.active = !tool.active;

			if (tool.active) {
				routeService.setTravelMode('walking');
			} else {
				routeService.setTravelMode('car');
			}
		},
	},
	{
		id: 'clearRoutes',
		isDefault: true,
		icon: 'directions_off',
		tooltip: t('ClearRoutes'),
		active: false,
		hidden: () => currentRoute.value == null,
		action: () => {
			clearRoutes();
			emit('toolbox-action', { toolId: 'clearRoutes' });
		},
	},

	{
		id: 'currentPosition',
		isDefault: true,
		icon: 'my_location',
		tooltip: t('ShowMyCurrentPosition'),
		active: false,
		hidden: () => false,
		action: async () => {
			await setUserPosition();
			addCurrentPositionMarker();
			zoomToUserPosition();
		},
	},
	{
		id: 'zoomIn',
		isDefault: true,
		isMapControl: true,
		icon: 'add',
		tooltip: t('ZoomIn'),
		active: false,
		className: 'with-separator',
		hidden: () => false,
		action: () => {
			if (iframeMap) {
				iframeMap.zoomIn();
			}
		},
	},
	{
		id: 'zoomOut',
		isDefault: true,
		isMapControl: true,
		icon: 'remove',
		tooltip: t('ZoomOut'),
		active: false,
		hidden: () => false,
		action: () => {
			if (iframeMap) {
				iframeMap.zoomOut();
			}
		},
	},
	{
		id: 'goToNextAppointment',
		icon: 'directions',
		tooltip: t('GoToNextAppointment'),
		active: false,
		oneShot: true,
		hidden: () => false,
		action: () => emit('toolbox-action', { toolId: 'goToNextAppointment' }),
	},
	{
		id: 'seeAllRoutes',
		icon: 'conversion_path',
		tooltip: t('SeeAllRoutes'),
		active: false,
		oneShot: true,
		hidden: () => false,
		action: () => emit('toolbox-action', { toolId: 'seeAllRoutes' }),
	},
	{
		id: 'openInApp',
		icon: 'navigation',
		tooltip: t('OpenInApp'),
		active: false,
		oneShot: true,
		hidden: () => false,
		action: () => emit('toolbox-action', { toolId: 'openInApp' }),
	},
]);

const bottomActionBarItems = computed(() => toolBoxTools.value.filter((t) => props.bottomToolBox.includes(t.id)));

const filtersComponentRef = ref(null);
const filterDirty = ref(false);

function translateDuration(durationText) {
	if (!durationText) return durationText;

	const userLang = iframeDataBridge.getCurrentUser()?.language || 'fr';

	const compactMatch = durationText.match(/^(\d+)h(\d+)$/);
	if (compactMatch) {
		const hours = parseInt(compactMatch[1], 10);
		const minutes = parseInt(compactMatch[2], 10);
		if (hours === 0) return `${minutes}min`;
		return `${hours}h${String(minutes).padStart(2, '0')}`;
	}

	return durationText
		.replace(/(\d+)\s*hours?/gi, '$1h')
		.replace(/(\d+)\s*hrs?/gi, '$1h')
		.replace(/(\d+)\s*minutes?/gi, '$1min')
		.replace(/(\d+)\s*mins?/gi, '$1min')
		.replace(/(\d+)\s*seconds?/gi, '$1sec')
		.replace(/(\d+)\s*secs?/gi, '$1sec')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/^0h\s*/, '');
}

function translateDistance(distanceText) {
	if (!distanceText) return distanceText;
	distanceText = distanceText.replace(/ /g, '');

	const userLang = iframeDataBridge.getCurrentUser()?.language || 'fr';
	if (userLang === 'fr') {
		return distanceText
			.replace(/(\d+(?:\.\d+)?)\s*miles?/gi, (match, num) => {
				const km = (parseFloat(num) * 1.60934).toFixed(1);
				return `${km}km`;
			})
			.replace(/(\d+(?:\.\d+)?)\s*feet?/gi, (match, num) => {
				const m = Math.round(parseFloat(num) * 0.3048);
				return `${m}m`;
			})
			.replace(/(\d+(?:\.\d+)?)\s*ft/gi, (match, num) => {
				const m = Math.round(parseFloat(num) * 0.3048);
				return `${m}m`;
			});
	}

	return distanceText;
}

async function initMapIfAllowed() {
	initMap();
	initAside();
	initToolBox();

	iframeDataBridge.getStorage().get('DELIVERYLOCATIONGEO', (data) => {
		deliveryLocationsGeo.value = data || [];
	});

	originalLocations.value = Array.from(props.locations);
	await setUserPosition();
}

function destroyMap() {
	clearHtmlMarkers();
	removeOrphanDragGhosts();
	teardownDragGhostWatchdog();

	if (iframeMap) {
		try {
			iframeMap.remove();
		} catch (error) {
			console.warn('MapLibre: error while removing map instance', error);
		}
		iframeMap = null;
	}

	if (iframeContainer && iframeContainer.parentNode) {
		iframeContainer.parentNode.removeChild(iframeContainer);
	}

	iframeContainer = null;
	offlineManager = null;
	offlineControl = null;
	isLoaded = false;
}

function reinitMap() {
	if (!isMapAllowed.value || iframeMap) return;
	initMapIfAllowed();
}

function onEvtOffline(state) {
	if (state !== 'up') {
		destroyMap();
		isMapAllowed.value = false;
	} else {
		const canShow = salesHelper.isGMAPActivated();
		if (canShow && !isMapAllowed.value) {
			isMapAllowed.value = true;
			nextTick(() => initMapIfAllowed());
		}
	}
}

onMounted(async () => {
	if (isMapAllowed.value && props.autoInit) {
		await initMapIfAllowed();
	}

	amplify.subscribe('evtOffline', onEvtOffline);
	amplify.subscribe('evtMapHighlight', onEvtMapHighlight);
	window.addEventListener('resize', onResize);
	observeContainerResize();
});

onBeforeUnmount(() => {
	destroyMap();

	if (markerDetailDrag.value) markerDetailDrag.value();
	amplify.unsubscribe('evtOffline', onEvtOffline);
	amplify.unsubscribe('evtMapHighlight', onEvtMapHighlight);
	window.removeEventListener('resize', onResize);
	disconnectContainerResize();
});

// #region Init

function initMap() {
	const iframe = document.querySelector('iframe[src^="/m.eagle"]');
	if (!iframe) return;

	iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
	setupDragGhostWatchdog();
	iframeLib = iframe.contentWindow.maplibregl;
	const originalMap = iframe.contentWindow.externisMap;
	const originalContainer = iframe.contentWindow.externisMapContainer;

	if (!iframeDataBridge.isOnline()) {
		console.warn('MapLibre: Map initialization skipped in offline mode');
		return;
	}

	if (!iframeLib || !originalContainer || !root.value) return;

	if (!originalMap) {
		console.warn('MapLibre: legacy map instance unavailable, skipping init');
		return;
	}

	// Clone the container for this map instance (shallow clone to avoid duplicating canvas)
	iframeContainer = originalContainer.cloneNode(false);
	iframeContainer.id = 'map-container-' + crypto.randomUUID();
	iframeContainer.style.width = '100%';
	iframeContainer.style.height = '100%';
	iframeContainer.style.position = 'relative';

	root.value.appendChild(iframeContainer);

	iframeMap = new iframeLib.Map({
		container: iframeContainer,
		style: originalMap.getStyle(),
		center: props.initialCenter,
		zoom: props.initialZoom,
		minZoom: props.minZoom,
		maxZoom: props.maxZoom,
		interactive: props.interactive,
	});

	iframeMap.scrollZoom.disable();

	root.value.addEventListener('mouseenter', () => iframeMap.scrollZoom.enable());
	root.value.addEventListener('mouseleave', () => iframeMap.scrollZoom.disable());

	const initializeMap = () => {
		isLoaded = true;
		ensureSourcesAndLayers();
		ensureHoverLayer();
		registerClusterClick();
		registerClusterHover();
		registerMapInteractions();

		const initialPoints = props.points?.length ? props.points : buildPointsFromLocations(props.locations);
		if (clusterEnabled.value) {
			updatePoints(initialPoints);
		} else {
			addHtmlMarkers(initialPoints);
		}

		if (pendingRouteWaypoints) {
			drawRouteAlongWaypoints(pendingRouteWaypoints);
		}

		styleUrl = iframe.contentWindow.externisMapStyleUrl;
		if (styleUrl) {
			offlineManager = new OfflineMapManager();
			offlineControl = new OfflineManagerControl(offlineManager, { styleUrl, mapLib: iframeLib });
		}

		if (props.autoFit) {
			fitAllWithUserPosition();
		}
	};

	if (iframeMap.isStyleLoaded()) {
		initializeMap();
	} else {
		iframeMap.once('load', initializeMap);
	}
}

function initAside() {
	const aside = iframeDocument.querySelector('.map-aside');
	if (aside) {
		blockMapEvents(aside);
	}
}

function initToolBox() {
	const toolBoxElement = iframeDocument.querySelector('.toolbox-container');
	if (toolBoxElement) {
		blockMapEvents(toolBoxElement);
	}
}

// #endregion

// #region Markers

function activateMarker(markerElement, point, cluster) {
	const markerId = point.id || point.deliveryLocationId || point.locationId;
	if (point.className && point.className == 'current-position-marker') return;
	if (selectedMarkerId.value === markerId) return;

	if (cluster) markerClickInProgress = true;
	selectedMarkerId.value = markerId;

	if (iframeMap._htmlMarkers) {
		iframeMap._htmlMarkers.forEach((m) => {
			if (m.getElement()) {
				m.getElement().classList.remove('selected');
			}
		});
	}

	markerElement.classList.add('selected');
	emit('marker-click', point);

	if (props.showPopupOnClick) {
		showDetailMarker(point);
	}

	if (cluster) {
		setTimeout(() => {
			markerClickInProgress = false;
		}, 1000);
	}
}

function bindMarkerActivation(markerElement, point, cluster) {
	markerElement.addEventListener('click', (e) => {
		e.stopPropagation();
		e.preventDefault();
		activateMarker(markerElement, point, cluster);
	});

	let touchStartX = 0;
	let touchStartY = 0;
	markerElement.addEventListener(
		'touchstart',
		(e) => {
			const touch = e.changedTouches[0];
			touchStartX = touch.clientX;
			touchStartY = touch.clientY;
		},
		{ passive: true }
	);

	markerElement.addEventListener('touchend', (e) => {
		if (isMarkerDragActive) return;
		const touch = e.changedTouches[0];
		if (!touch) return;
		const dx = touch.clientX - touchStartX;
		const dy = touch.clientY - touchStartY;
		if (Math.sqrt(dx * dx + dy * dy) > 10) return;
		e.preventDefault();
		e.stopPropagation();
		activateMarker(markerElement, point, cluster);
	});
}

async function addHtmlMarkers(points) {
	if (!iframeLib) return;
	// Each render pass claims a token. createHtmlMarkerElement awaits per-marker image loads,
	// so several passes (e.g. full set then filtered set) can run concurrently and finish out of
	// order. Without this guard the slower pass commits last and leaves stale/unfiltered markers.
	const renderToken = ++markerRenderToken;
	clusterModeActive = false;
	buildSearchIndexFromPoints(points);
	clearHtmlMarkers();

	// Clear the GeoJSON source so stale cluster circles don't keep rendering
	if (iframeMap && iframeMap.getSource('points')) {
		setSourceData('points', { type: 'FeatureCollection', features: [] });
	}

	for (const point of points) {
		const ll = Array.isArray(point) ? point : [point.lng, point.lat];

		try {
			const markerElement = await createHtmlMarkerElement(point);
			if (renderToken !== markerRenderToken) return;
			const marker = new iframeLib.Marker({
				element: markerElement,
				anchor: 'bottom',
			})
				.setLngLat(ll)
				.addTo(iframeMap);

			if (!iframeMap._htmlMarkers) {
				iframeMap._htmlMarkers = [];
			}
			iframeMap._htmlMarkers.push(marker);

			bindMarkerActivation(markerElement, point, false);

			setupMarkerDrag(markerElement, point);
		} catch (error) {
			console.error('Error creating marker for point:', point, error);
		}
	}
}

// Markers are built imperatively, outside Vue's renderer, so the icon markup that
// <v-icon> would emit has to be produced by hand.
function createMarkerIcon(name) {
	const icon = document.createElement('i');
	icon.className = 'v-icon material-symbols-outlined';
	icon.textContent = name;
	return icon;
}

async function createHtmlMarkerElement(point) {
	const el = document.createElement('div');
	const account = iframeDataBridge
		.getEcosVM()
		.accounts()
		.find((a) => a.id === point.accountId);
	const hasAccountLogo = point.accountId && account && account.imageUId && account.imageUId !== '00000000-0000-0000-0000-000000000000' && point.deliveryLocationTypeId === 2;

	if (point.className) {
		el.className = 'marker ' + point.className;
	} else {
		el.className = 'marker delivery-location-marker ' + (hasAccountLogo ? 'with-logo' : 'no-logo') + ' hidden-marker';
	}

	const markerId = point.id || point.deliveryLocationId || point.locationId;
	if (markerId != null) {
		el.dataset.markerId = markerId;
	}

	if (point.color && !hasAccountLogo) {
		el.style.backgroundColor = point.color;
	}

	if (point.sequence) {
		el.classList.add('has-sequence');
		const badge = document.createElement('div');
		badge.className = 'marker-sequence-badge';
		badge.textContent = point.sequence;
		el.appendChild(badge);
	}

	if (hasAccountLogo) {
		const img = document.createElement('img');
		img.draggable = false;

		try {
			const imageUrl = await getAccountImageUrl(account.imageUId);
			if (imageUrl) {
				img.src = imageUrl;
			} else {
				const placeholder = createMarkerIcon(point.icon || 'storefront');
				el.appendChild(placeholder);
				return el;
			}
		} catch (error) {
			console.error('Error loading account image:', error);
			const placeholder = createMarkerIcon(point.icon || 'storefront');
			el.appendChild(placeholder);
			return el;
		}

		img.alt = account.Name || point.title || '';
		el.appendChild(img);
	} else {
		const placeholder = createMarkerIcon(point.icon || 'storefront');
		el.appendChild(placeholder);
	}

	return el;
}

function clearHtmlMarkers() {
	if (!iframeMap || !iframeMap._htmlMarkers) return;
	iframeMap._htmlMarkers = iframeMap._htmlMarkers.filter((marker) => {
		const el = marker && marker.getElement ? marker.getElement() : null;
		if (!el) return false;

		if (el.classList.contains('current-position-marker')) {
			return true;
		}

		marker.remove();
		return false;
	});
}

// Safety net: on iPad a pinch/zoom with fingers on two markers can leave a drag ghost orphaned
// because the pointercancel/pointerup that runs the per-marker cleanup isn't reliably delivered to
// the captured element. This sweeps any lingering ghost and resets the drag state unconditionally.
function removeOrphanDragGhosts() {
	if (iframeDocument) iframeDocument.querySelectorAll('.marker-drag-ghost').forEach((el) => el.remove());
	document.querySelectorAll('.marker-drag-ghost').forEach((el) => el.remove());
	if (isMarkerDragActive) {
		isMarkerDragActive = false;
		iframeMap?.dragPan?.enable();
	}
}

function onGlobalTouchEnd(e) {
	if (e.touches && e.touches.length > 0) return;
	removeOrphanDragGhosts();
}

function setupDragGhostWatchdog() {
	if (!iframeDocument || dragGhostWatchdogDoc === iframeDocument) return;
	teardownDragGhostWatchdog();
	dragGhostWatchdogDoc = iframeDocument;
	dragGhostWatchdogDoc.addEventListener('pointercancel', removeOrphanDragGhosts, { passive: true });
	dragGhostWatchdogDoc.addEventListener('touchend', onGlobalTouchEnd, { passive: true });
	dragGhostWatchdogDoc.addEventListener('touchcancel', onGlobalTouchEnd, { passive: true });
}

function teardownDragGhostWatchdog() {
	if (!dragGhostWatchdogDoc) return;
	dragGhostWatchdogDoc.removeEventListener('pointercancel', removeOrphanDragGhosts);
	dragGhostWatchdogDoc.removeEventListener('touchend', onGlobalTouchEnd);
	dragGhostWatchdogDoc.removeEventListener('touchcancel', onGlobalTouchEnd);
	dragGhostWatchdogDoc = null;
}

function setupMarkerDrag(markerElement, point) {
	if (!props.enableMarkerDrag) return;

	// A marker already scheduled in the day carries an order/sequence badge. Re-dragging it duplicates
	// the appointment and freezes the interaction, so such markers are made non-draggable (still clickable).
	if (point.sequence || markerElement.classList.contains('has-sequence')) return;

	markerElement.style.touchAction = 'none';
	markerElement.querySelectorAll('*').forEach((el) => {
		el.style.touchAction = 'none';
	});

	let ghost = null;
	let isDragging = false;
	let startX = 0;
	let startY = 0;
	let activePointerId = null;
	const DRAG_THRESHOLD = 6;

	function onPointerMove(e) {
		if (activePointerId !== null && e.pointerId !== activePointerId) return;
		const dx = e.clientX - startX;
		const dy = e.clientY - startY;

		if (!isDragging && Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
			if (isMarkerDragActive) return;
			isDragging = true;
			isMarkerDragActive = true;
			emit('marker-drag-start', { markerData: point });
			iframeMap?.dragPan?.disable();
			const ownerDoc = iframeDataBridge.getIframeWindow()?.document || document;
			ghost = ownerDoc.createElement('div');
			ghost.className = 'marker-drag-ghost';
			const markerVisual = markerElement.querySelector('img, .v-icon');
			if (markerVisual) {
				ghost.appendChild(markerVisual.cloneNode(true));
			}
			const label = point.label || point.title || '';
			if (label) {
				const labelEl = ownerDoc.createElement('span');
				labelEl.textContent = label;
				ghost.appendChild(labelEl);
			}
			if (point.color) ghost.style.setProperty('--ghost-color', point.color);
			ownerDoc.body.appendChild(ghost);

			toggleFcBusinessElements(true);
		}

		if (isDragging && ghost) {
			ghost.style.left = `${e.clientX - ghost.offsetWidth / 2}px`;
			ghost.style.top = `${e.clientY - ghost.offsetHeight - 8}px`;
		}
	}

	function cleanup(e) {
		if (e && e.pointerId != null && activePointerId !== null && e.pointerId !== activePointerId) return;
		isDragging = false;
		isMarkerDragActive = false;
		activePointerId = null;
		iframeMap?.dragPan?.enable();
		if (ghost) {
			ghost.remove();
			ghost = null;
		}
		markerElement.removeEventListener('pointermove', onPointerMove);
		markerElement.removeEventListener('pointerup', onPointerUp);
		markerElement.removeEventListener('pointercancel', cleanup);
	}

	function onPointerUp(e) {
		if (activePointerId !== null && e.pointerId !== activePointerId) return;
		const wasDragging = isDragging;
		cleanup();
		if (wasDragging) {
			// Prevent the click event that fires after pointerup
			const preventClick = (ev) => {
				ev.stopPropagation();
				ev.preventDefault();
				markerElement.removeEventListener('click', preventClick, true);
			};
			markerElement.addEventListener('click', preventClick, true);

			// Only emit if dropped onto .fc-body (calendar may be inside an iframe)
			let dropEl = document.elementFromPoint(e.clientX, e.clientY);
			if (dropEl?.tagName === 'IFRAME') {
				try {
					const rect = dropEl.getBoundingClientRect();
					dropEl = dropEl.contentDocument?.elementFromPoint(e.clientX - rect.left, e.clientY - rect.top) ?? dropEl;
				} catch (_) {
					/* cross-origin, ignore */
				}
			}

			if (!dropEl?.closest('.fc-body')) {
				emit('marker-drag-cancel', { markerData: point });
				toggleFcBusinessElements(false);
				return;
			}

			emit('marker-drag-drop', { markerData: point, x: e.clientX, y: e.clientY });
			toggleFcBusinessElements(false);
		}
	}

	function toggleFcBusinessElements(hide) {
		const iframeDocument = iframeDataBridge.getIframeWindow().document;
		if (iframeDocument && iframeDocument.querySelector('.fc-nonbusiness')) {
			iframeDocument.querySelectorAll('.fc-nonbusiness').forEach((el) => {
				el.style.display = hide ? 'none' : '';
			});
		}
	}

	markerElement.addEventListener('pointerdown', (e) => {
		if (e.button !== 0) return;
		if (!e.isPrimary || isMarkerDragActive || activePointerId !== null) return;
		activePointerId = e.pointerId;
		startX = e.clientX;
		startY = e.clientY;
		isDragging = false;
		markerElement.setPointerCapture(e.pointerId);
		markerElement.addEventListener('pointermove', onPointerMove);
		markerElement.addEventListener('pointerup', onPointerUp);
		markerElement.addEventListener('pointercancel', cleanup);
	});
}

function bindHtmlMarkers() {
	if (!iframeMap || !iframeLib || !clusterEnabled.value) return;

	iframeMap.on('idle', () => {
		if (!clusterModeActive) return;
		if (markerClickInProgress) return;
		if (isMarkerDragActive) return;
		clearHtmlMarkers();

		const features = iframeMap.querySourceFeatures('points', {
			filter: ['!', ['has', 'point_count']],
		});

		const processFeatures = async () => {
			for (const feature of features) {
				if (!feature.geometry || !feature.geometry.coordinates) continue;

				const [lng, lat] = feature.geometry.coordinates;
				const featureProps = feature.properties;

				try {
					const markerElement = await createHtmlMarkerElement(featureProps);
					if (selectedMarkerId.value && featureProps.id === selectedMarkerId.value) {
						markerElement.classList.add('selected');
					}

					const marker = new iframeLib.Marker({
						element: markerElement,
						anchor: 'bottom',
					})
						.setLngLat([lng, lat])
						.addTo(iframeMap);

					if (!iframeMap._htmlMarkers) {
						iframeMap._htmlMarkers = [];
					}
					iframeMap._htmlMarkers.push(marker);

					bindMarkerActivation(markerElement, featureProps, true);

					setupMarkerDrag(markerElement, featureProps);
				} catch (error) {
					console.error('Error creating marker:', error);
				}
			}
		};

		processFeatures();
	});
}

async function addCurrentPositionMarker(currentPosition = null) {
	if (!iframeLib || !iframeMap || (!userCurrentPosition.value && !currentPosition)) return;
	userCurrentPosition.value = currentPosition || userCurrentPosition.value;
	removeCurrentPositionMarker();

	const markerElement = await createHtmlMarkerElement({
		className: 'current-position-marker',
		color: '#4CAF50',
		icon: 'my_location',
		sequence: 1,
	});
	markerElement.classList.add('pulse');
	const marker = new iframeLib.Marker({
		element: markerElement,
		anchor: 'bottom',
	})
		.setLngLat([userCurrentPosition.value.lng, userCurrentPosition.value.lat])
		.addTo(iframeMap);

	if (!iframeMap._htmlMarkers) {
		iframeMap._htmlMarkers = [];
	}
	iframeMap._htmlMarkers.push(marker);
}

function removeCurrentPositionMarker() {
	if (!iframeMap || !iframeMap._htmlMarkers) return;

	iframeMap._htmlMarkers = iframeMap._htmlMarkers.filter((marker) => {
		const el = marker.getElement();
		if (el && el.classList.contains('current-position-marker')) {
			marker.remove();
			return false;
		}
		return true;
	});
}

// #endregion

// #region GeoJSON builders

function fc(features) {
	return { type: 'FeatureCollection', features: features || [] };
}

function buildGeoJson(points) {
	const features = points.map((p) => ({
		type: 'Feature',
		geometry: {
			type: 'Point',
			coordinates: Array.isArray(p) ? p : [p.lng, p.lat],
		},
		properties: {
			id: p.id ?? crypto.randomUUID(),
			title: p.title ?? '',
			color: p.color ?? '#11b4da',
			size: Number(p.size ?? 6),
			...p,
		},
	}));

	buildSearchIndex(features);

	return fc(features);
}

function buildLines(lines) {
	return {
		type: 'FeatureCollection',
		features: (lines || []).map((l) => ({
			type: 'Feature',
			geometry: { type: 'LineString', coordinates: l.path || [] },
			properties: {
				id: l.id ?? crypto.randomUUID(),
				color: l.color ?? '#1976D2',
				width: Number(l.width ?? 3),
				hasDash: Array.isArray(l.dash) && l.dash.length > 0,
				dashPattern: Array.isArray(l.dash) ? l.dash : null,
				...(l.props || {}),
			},
		})),
	};
}

function buildPolygons(polys) {
	return fc(
		(polys || []).map((p) => ({
			type: 'Feature',
			geometry: { type: 'Polygon', coordinates: p.rings || [] },
			properties: {
				id: p.id ?? crypto.randomUUID(),
				fill: p.fill ?? '#1976D2',
				stroke: p.stroke ?? '#1976D2',
				width: Number(p.width ?? 2),
				opacity: Number(p.opacity ?? 0.3),
				...(p.props || {}),
			},
		}))
	);
}

function buildHeat(heat) {
	return fc(
		(heat || []).map((h) => ({
			type: 'Feature',
			geometry: { type: 'Point', coordinates: [Number(h.lng), Number(h.lat)] },
			properties: { weight: Number(h.weight ?? 1) },
		}))
	);
}

// #endregion

// #region Layers helpers

function addClusterLayers() {
	const layers = [
		{
			id: 'cluster-shadow',
			type: 'circle',
			filter: ['has', 'point_count'],
			paint: {
				'circle-color': 'rgba(0, 0, 0, 0.192)',
				'circle-radius': ['step', ['get', 'point_count'], 17, 10, 22, 30, 27],
				'circle-opacity': 1,
				'circle-translate': [2, 2],
			},
		},
		{
			id: 'clusters',
			type: 'circle',
			filter: ['has', 'point_count'],
			paint: {
				'circle-color': '#1c79bb',
				'circle-radius': ['step', ['get', 'point_count'], 15, 10, 20, 30, 25],
				'circle-opacity': 1,
				'circle-stroke-width': 4,
				'circle-stroke-color': '#fff',
			},
		},
		{
			id: 'cluster-count',
			type: 'symbol',
			filter: ['has', 'point_count'],
			layout: {
				'text-field': '{point_count_abbreviated}',
				'text-font': ['Noto Sans Regular'],
				'text-size': ['step', ['get', 'point_count'], 10, 12, 14],
			},
			paint: { 'text-color': '#fff' },
		},
	];

	layers.forEach((layer) => {
		if (!iframeMap.getLayer(layer.id)) {
			iframeMap.addLayer({ ...layer, source: 'points' });
		}
	});

	bindHtmlMarkers();
}

function addPolylineLayers() {
	if (!iframeMap.getLayer('polylines')) {
		iframeMap.addLayer({
			id: 'polylines',
			type: 'line',
			source: 'lines',
			filter: ['==', ['get', 'hasDash'], false],
			paint: {
				'line-color': ['coalesce', ['get', 'color'], '#1976D2'],
				'line-width': ['coalesce', ['get', 'width'], 3],
			},
		});
	}

	if (!iframeMap.getLayer('polylines-dash')) {
		iframeMap.addLayer({
			id: 'polylines-dash',
			type: 'line',
			source: 'lines',
			filter: ['==', ['get', 'hasDash'], true],
			paint: {
				'line-color': ['coalesce', ['get', 'color'], '#1976D2'],
				'line-width': ['coalesce', ['get', 'width'], 3],
				'line-dasharray': [2, 2],
			},
		});
	}
}

function addPolygonLayers() {
	if (!iframeMap.getLayer('polygons-fill')) {
		iframeMap.addLayer({
			id: 'polygons-fill',
			type: 'fill',
			source: 'polys',
			paint: {
				'fill-color': ['coalesce', ['get', 'fill'], '#1976D2'],
				'fill-opacity': ['coalesce', ['get', 'opacity'], 0.3],
			},
		});
	}
	if (!iframeMap.getLayer('polygons-outline')) {
		iframeMap.addLayer({
			id: 'polygons-outline',
			type: 'line',
			source: 'polys',
			paint: {
				'line-color': ['coalesce', ['get', 'stroke'], '#1976D2'],
				'line-width': ['coalesce', ['get', 'width'], 2],
			},
		});
	}
}

function addHeatmapLayer() {
	if (!iframeMap.getLayer('heatmap')) {
		iframeMap.addLayer({
			id: 'heatmap',
			type: 'heatmap',
			source: 'heat',
			maxzoom: 18,
			paint: {
				'heatmap-weight': ['coalesce', ['get', 'weight'], 1],
				'heatmap-radius': 20,
				'heatmap-intensity': 1,
				'heatmap-opacity': 0.6,
			},
		});
	}
}

// #endregion

// #region Sources update

function toIframeRealm(value) {
	const iframeWindow = iframeDataBridge.getIframeWindow();
	return iframeWindow ? iframeWindow.JSON.parse(JSON.stringify(value)) : value;
}

function addGeoJsonSource(id, options) {
	iframeMap.addSource(id, { ...options, data: toIframeRealm(options.data) });
}

function setSourceData(id, data) {
	iframeMap.getSource(id).setData(toIframeRealm(data));
}

function ensureSourcesAndLayers() {
	if (!iframeMap.getSource('route')) {
		addGeoJsonSource('route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
	}

	if (!iframeMap.getLayer('route')) {
		const routeLayer = {
			id: 'route',
			type: 'line',
			source: 'route',
			paint: {
				'line-color': ['case', ['has', 'color'], ['get', 'color'], '#1976D2'],
				'line-width': 4,
				'line-opacity': 0.8,
			},
		};

		if (iframeMap.getLayer('clusters')) {
			iframeMap.addLayer(routeLayer, 'clusters');
		} else {
			iframeMap.addLayer(routeLayer);
		}
	}
}

const hoverSourceId = 'hover-source';
const hoverLayerId = 'hover-point';

function ensureHoverLayer() {
	if (!iframeMap.getSource(hoverSourceId)) {
		addGeoJsonSource(hoverSourceId, {
			type: 'geojson',
			data: { type: 'FeatureCollection', features: [] },
		});
	}
	if (!iframeMap.getLayer(hoverLayerId)) {
		iframeMap.addLayer({
			id: hoverLayerId,
			type: 'circle',
			source: hoverSourceId,
			paint: {
				'circle-color': '#1c79bb',
				'circle-radius': 10,
				'circle-stroke-width': 4,
				'circle-stroke-color': '#ffffff',
				'circle-opacity': 0,
				'circle-radius-transition': { duration: 200, delay: 0 },
				'circle-opacity-transition': { duration: 150, delay: 0 },
				'circle-stroke-width-transition': { duration: 150, delay: 0 },
			},
		});
	}
}

// #endregion

// #region Interactions

function registerClusterClick() {
	iframeMap.on('click', 'clusters', async (e) => {
		const features = iframeMap.queryRenderedFeatures(e.point, { layers: ['clusters'] });
		if (!features.length) return;
		const clusterId = features[0].properties.cluster_id;
		try {
			const zoom = await iframeMap.getSource('points').getClusterExpansionZoom(clusterId);
			iframeMap.easeTo({ center: features[0].geometry.coordinates, zoom });
		} catch (err) {
			console.error('Error expanding cluster:', err);
		}
	});
}

function registerClusterHover() {
	iframeMap.on('mouseenter', 'clusters', () => {
		iframeMap.getCanvas().style.cursor = 'pointer';
	});
	iframeMap.on('mouseleave', 'clusters', () => {
		iframeMap.getCanvas().style.cursor = '';
	});
}

function registerMapInteractions() {
	iframeMap.on('moveend', () => {
		const b = iframeMap.getBounds();
		emit('bounds-changed', {
			sw: [b.getWest(), b.getSouth()],
			ne: [b.getEast(), b.getNorth()],
		});
	});
}

// #endregion

// #region Action buttons methods

function editDeliveryLocation() {
	if (!detailMarkerData.value.id) return;
	emit('edit-delivery-location', detailMarkerData.value.id);
}

function callPhoneNumber() {
	if (!detailMarkerData.value.phoneNumber) return;
	const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

	if (isMobile) {
		window.open(`tel:${detailMarkerData.value.phoneNumber}`, '_self');
	} else {
		navigator.clipboard?.writeText(detailMarkerData.value.phoneNumber);
		emit('phone-number-copied', detailMarkerData.value.phoneNumber);
	}
}

function addNewVisit() {
	if (!detailMarkerData.value.id) return;
	emit('add-new-visit', {
		deliveryLocationId: detailMarkerData.value.id,
		deliveryLocationName: detailMarkerData.value.title,
		selectedDate: props.selectedDate,
	});
}

async function showDirections(routeDataProvided = null) {
	if (isTouchMode) {
		dragSenderMode.value = 'mini';
	}

	if (currentRoute.value) {
		clearRoutes();
	}

	if ((!detailMarkerData.value.lat || !detailMarkerData.value.lng) && (!routeDataProvided || !routeDataProvided.features || routeDataProvided.features.length === 0)) {
		return;
	}

	try {
		searchingPosition.value = true;
		try {
			await setUserPosition();
		} finally {
			searchingPosition.value = false;
		}

		const origin = [userCurrentPosition.value.lng, userCurrentPosition.value.lat];
		const destination = [detailMarkerData.value.lng, detailMarkerData.value.lat];
		const routeData = routeDataProvided && routeDataProvided.features ? routeDataProvided : await calculateRoute([origin, destination]);

		if (routeData && routeData.features && routeData.features.length > 0) {
			const properties = routeData.features[0].properties;
			if (properties) {
				routeData.durationText = properties.duration || '';
				routeData.distanceText = properties.distance || '';
			}
			currentRoute.value = routeData;

			if (iframeMap.getSource('route')) {
				setSourceData('route', routeData);
			} else {
				addGeoJsonSource('route', {
					type: 'geojson',
					data: routeData,
				});
			}

			const coordinates = routeData.features[0].geometry.coordinates;
			const bounds = coordinates.reduce(
				(bounds, coord) => {
					return bounds.extend(coord);
				},
				new iframeLib.LngLatBounds(coordinates[0], coordinates[0])
			);

			iframeMap.fitBounds(bounds, {
				padding: {
					top: 100,
					right: 50,
					bottom: 50,
					left: iframeDocument.querySelector('.marker-detail-container.visible') ? iframeDocument.querySelector('.marker-detail-container.visible').offsetWidth + 50 : 50,
				},
			});
		} else {
			toast(t('RouteCalculationFailed') || 'Impossible de calculer le trajet', 'error', true);
		}
	} catch (error) {
		console.error('Error calculating route:', error);
		if (error.code === 1) {
			toast(t('LocationAccessDenied') || 'Accès à la position refusé', 'error', true);
		} else if (error.code === 2) {
			toast(t('LocationUnavailable') || 'Position non disponible', 'error', true);
		} else if (error.code === 3) {
			toast(t('LocationTimeout') || "Délai d'attente dépassé pour obtenir la position", 'error', true);
		} else {
			toast(t('RouteCalculationError') || 'Erreur lors du calcul du trajet', 'error', true);
		}
	}
}

async function navigateToLocation(lat, lng, _title = '', fallbackCoords = null) {
	try {
		let origin;

		try {
			searchingPosition.value = true;
			try {
				await setUserPosition();
			} finally {
				searchingPosition.value = false;
			}
			origin = [userCurrentPosition.value.lng, userCurrentPosition.value.lat];
			console.log('Using user geolocation as the starting point');
		} catch (geoError) {
			if (fallbackCoords) {
				origin = [fallbackCoords.lng, fallbackCoords.lat];
				console.log('Geolocation unavailable, using fallback point as the starting point');
			} else {
				throw geoError;
			}
		}

		const destination = [lng, lat];
		const routeData = await calculateRoute([origin, destination]);

		if (routeData && routeData.features && routeData.features.length > 0) {
			const properties = routeData.features[0].properties;
			if (properties) {
				routeData.durationText = properties.duration || '';
				routeData.distanceText = properties.distance || '';
			}
			currentRoute.value = routeData;

			if (iframeMap.getSource('route')) {
				setSourceData('route', routeData);
			} else {
				addGeoJsonSource('route', {
					type: 'geojson',
					data: routeData,
				});
			}

			const coordinates = routeData.features[0].geometry.coordinates;
			const bounds = coordinates.reduce(
				(bounds, coord) => {
					return bounds.extend(coord);
				},
				new iframeLib.LngLatBounds(coordinates[0], coordinates[0])
			);

			iframeMap.fitBounds(bounds, {
				padding: {
					top: 100,
					right: 50,
					bottom: 50,
					left: iframeDocument.querySelector('.marker-detail-container.visible') ? iframeDocument.querySelector('.marker-detail-container.visible').offsetWidth + 50 : 50,
				},
			});
		} else {
			toast(t('RouteCalculationFailed') || 'Impossible de calculer le trajet', 'error', true);
		}
	} catch (error) {
		console.error('Error calculating route to location:', error);
		if (error.code === 1) {
			toast(t('LocationAccessDenied') || 'Accès à la position refusé', 'error', true);
		} else if (error.code === 2) {
			toast(t('LocationUnavailable') || 'Position non disponible', 'error', true);
		} else if (error.code === 3) {
			toast(t('LocationTimeout') || "Délai d'attente dépassé pour obtenir la position", 'error', true);
		} else {
			toast(t('RouteCalculationError') || 'Erreur lors du calcul du trajet', 'error', true);
		}
	}
}

function openInMapsApplication() {
	if (!detailMarkerData.value.lat || !detailMarkerData.value.lng) return;

	const destination = `${detailMarkerData.value.lat},${detailMarkerData.value.lng}`;
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
	const isAndroid = /Android/.test(navigator.userAgent);
	const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

	if (isMobile) {
		if (isIOS) {
			const appleMapUrl = `maps://maps.apple.com/?q=${destination}`;
			const webFallback = `https://www.google.com/maps/search/?api=1&query=${destination}`;

			window.location.href = appleMapUrl;

			setTimeout(() => {
				window.open(webFallback, '_blank');
			}, 1500);

			return;
		}

		if (isAndroid) {
			const geoUrl = `geo:0,0?q=${destination}`;
			const webFallback = `https://www.google.com/maps/search/?api=1&query=${destination}`;

			window.location.href = geoUrl;

			setTimeout(() => {
				window.open(webFallback, '_blank');
			}, 1500);

			return;
		}
	}

	const webUrl = `https://www.google.com/maps/search/?api=1&query=${destination}`;
	window.open(webUrl, '_blank');
}

async function setUserPosition() {
	try {
		if (navigator.geolocation) {
			const position = await new Promise((resolve, reject) => {
				navigator.geolocation.getCurrentPosition(resolve, reject, {
					timeout: 5000,
					maximumAge: 30000,
				});
			});
			userCurrentPosition.value = {
				lat: position.coords.latitude,
				lng: position.coords.longitude,
			};
			geolocationActive.value = true;
			addCurrentPositionMarker();
		} else {
			geolocationActive.value = false;
		}
	} catch (error) {
		geolocationActive.value = false;
		let userHomeGeo = deliveryLocationsGeo.value.find((g) => g.deliveryLocationId === user.deliveryLocationHomeId);
		if (userHomeGeo && userHomeGeo.latitude && userHomeGeo.longitude) {
			userCurrentPosition.value = {
				lat: userHomeGeo.latitude,
				lng: userHomeGeo.longitude,
			};
		}
	}
}

function getUserPosition() {
	return userCurrentPosition.value;
}

function isGeolocationActive() {
	return geolocationActive.value;
}

async function refreshUserPosition() {
	await setUserPosition();
}

function zoomToUserPosition() {
	if (!iframeMap || !userCurrentPosition.value) return;

	iframeMap.easeTo({
		center: [userCurrentPosition.value.lng, userCurrentPosition.value.lat],
		zoom: Math.max(iframeMap.getZoom(), 14),
	});
}

async function calculateRoute(waypoints) {
	return await routeService.getRouteWithFallback(waypoints);
}

async function getRouteSummary(waypoints) {
	const geoJson = await routeService.getRouteWithFallback(waypoints);
	const props = geoJson?.features?.[0]?.properties || {};
	return {
		distance: translateDistance(props.distance || ''),
		duration: translateDuration(props.duration || ''),
		distanceValue: props.distance_value || null,
		durationValue: props.duration_value || null,
	};
}

function clearRoutes() {
	if (!iframeMap || !iframeMap.getSource('route')) return;

	currentRoute.value = null;
	setSourceData('route', {
		type: 'FeatureCollection',
		features: [],
	});
}

async function drawRouteAlongWaypoints(waypoints) {
	if (!Array.isArray(waypoints) || waypoints.length < 2) return;

	if (!iframeMap || !isLoaded) {
		pendingRouteWaypoints = waypoints;
		return;
	}

	pendingRouteWaypoints = null;

	if (currentRoute.value) {
		clearRoutes();
	}

	ensureSourcesAndLayers();

	try {
		const routeData = await calculateRoute(waypoints);

		if (routeData && routeData.features && routeData.features.length > 0) {
			const properties = routeData.features[0].properties;
			if (properties) {
				routeData.durationText = properties.duration || '';
				routeData.distanceText = properties.distance || '';
			}
			currentRoute.value = routeData;

			if (iframeMap.getSource('route')) {
				setSourceData('route', routeData);
			}
		}
	} catch (error) {
		console.error('Error calculating route along waypoints:', error);
	}
}

// #endregion

// #region Legend

function toggleLegend() {
	legendVisible.value = !legendVisible.value;
}

// #endregion

// #region Filters & Actions

function locationFilterChanged(filterData) {
	filterDirty.value = filterData.dirty;
	emit('filters-changed', filterData.filteredLocations);
	hideDetailMarker();
}

// #endregion

// #region Marker detail

async function showDetailMarker(propsData) {
	detailMarkerLoading.value = true;
	detailMarkerVisible.value = true;
	selectedMarkerId.value = propsData.id;

	detailMarkerData.value.title = propsData.title || propsData.label || '';
	detailMarkerData.value.id = propsData.id;
	detailMarkerData.value.lat = propsData.lat || propsData._geo?.latitude;
	detailMarkerData.value.lng = propsData.lng || propsData._geo?.longitude;
	detailMarkerData.value.sps = null;
	detailMarkerData.value.isShared = isSharedDeliveryLocationId(propsData.id);
	detailMarkerData.value.accountLogo = null;
	detailMarkerData.value.accountName = '';
	detailMarkerData.value.fields = {};
	detailMarkerData.value.kpis = [];
	detailMarkerData.value.lastVisitComment = '';

	if (typeof props.detailMarkerRenderer === 'function') {
		await props.detailMarkerRenderer(propsData, iframeDocument, detailMarkerData);
		if (!detailMarkerData.value.lat && (propsData.lat || propsData._geo?.latitude)) {
			detailMarkerData.value.lat = propsData.lat || propsData._geo?.latitude;
			detailMarkerData.value.lng = propsData.lng || propsData._geo?.longitude;
		}
	}
	// Default content
	else {
		const fields = {};
		Object.entries(propsData).forEach(([key, value]) => {
			if (!['id', 'title', 'label', 'size', 'color', 'icon', 'sps', 'accountId', 'accountLogo'].includes(key) && value) {
				fields[key] = value;
			}
		});
		detailMarkerData.value.fields = fields;
	}

	detailMarkerLoading.value = false;
	dragSenderMode.value = 'mid';
	await nextTick();
	await resizeDetailMarkerContent();
}

function onEvtMapHighlight({ dlId }) {
	const all = props.points?.length ? props.points : buildPointsFromLocations(props.locations);
	const found = all.find((p) => String(p.id) === String(dlId));
	if (!found) return;
	if (iframeMap) {
		if (found.lng != null && found.lat != null) {
			iframeMap.easeTo({ center: [found.lng, found.lat] });
		}
		if (iframeMap._htmlMarkers) {
			iframeMap._htmlMarkers.forEach((m) => {
				const el = m.getElement();
				if (!el) return;
				if (String(el.dataset.markerId) === String(dlId)) {
					el.classList.add('selected');
				} else {
					el.classList.remove('selected');
				}
			});
		}
	}
	showDetailMarker(found);
}

function hideDetailMarker(e) {
	if (e) e.stopPropagation();
	detailMarkerHiding.value = true;

	if (iframeMap && iframeMap._htmlMarkers) {
		iframeMap._htmlMarkers.forEach((m) => {
			if (m.getElement()) {
				m.getElement().classList.remove('selected');
			}
		});
	}
	selectedMarkerId.value = null;
	emit('marker-detail-hidden');

	setTimeout(() => {
		detailMarkerVisible.value = false;
		detailMarkerHiding.value = false;
		detailMarkerData.value = {
			title: '',
			sps: null,
			accountLogo: null,
			accountName: '',
			fields: {},
		};
	}, 500);
}

function blockMapEvents(el) {
	const events = ['wheel', 'mousewheel', 'touchstart', 'touchmove', 'touchend', 'pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'dblclick'];

	events.forEach((eventName) => {
		el.addEventListener(
			eventName,
			(e) => {
				e.stopPropagation();
			},
			{ passive: false }
		);
	});
}

async function resizeDetailMarkerContent() {
	if (!iframeDocument || !root.value) return;

	const mapAside = root.value.parentElement.querySelector('.map-aside');
	const markerDetailSearchElement = root.value.parentElement.querySelector('.map-aside .search-container');
	const markerDetailHeaderElement = root.value.parentElement.querySelector('.map-aside .marker-detail-header');
	const markerDetailActionsElement = root.value.parentElement.querySelector('.map-aside .marker-detail-actions');
	const markerDetailContentElement = root.value.parentElement.querySelector('.map-aside .marker-detail-content');
	if (markerDetailContentElement) {
		await nextTick();
		let height = 0;

		Array.from(markerDetailContentElement.children).forEach((child) => {
			height += child.offsetHeight + 20;
		});

		let maxHeight = mapAside.offsetHeight - (markerDetailSearchElement ? markerDetailSearchElement.offsetHeight : 0) - (markerDetailHeaderElement ? markerDetailHeaderElement.offsetHeight : 0) - (markerDetailActionsElement ? markerDetailActionsElement.offsetHeight : 0) - 80;
		markerDetailContentElement.style.minHeight = `${maxHeight < height ? maxHeight : height}px`;
	}
}

function onMarkerDetailHeaderClick() {
	if (!isTouchMode.value) return;
	if (dragSenderIsDragging.value) return;
	if (typeof markerDetailDrag.value?.setMode === 'function') {
		markerDetailDrag.value.setMode(dragSenderMode.value == 'full' ? 'mini' : dragSenderMode.value == 'mini' ? 'mid' : 'full');
	}
}

watch(
	() => detailMarkerVisible.value,
	async (v) => {
		if (!v) return;
		await nextTick();
		if (markerDetailDrag.value) markerDetailDrag.value();
		if (!isTouchMode.value) return;
		const panelEl = markerDetailRef.value;
		const handleEl = markerDetailHeaderRef.value;
		markerDetailDrag.value = dragBottomSheetHandler(panelEl, handleEl, {
			isTouchMode,
			dragSenderMode,
			dragSenderIsDragging,
			iframeDocument,
		});
	},
	{ flush: 'post' }
);

watch(
	() => isTouchMode.value,
	async (v) => {
		if (!detailMarkerVisible.value) return;
		await nextTick();
		if (markerDetailDrag.value) markerDetailDrag.value();
		markerDetailDrag.value = null;
		if (!v) return;
		const panelEl = markerDetailRef.value;
		const handleEl = markerDetailHeaderRef.value;
		markerDetailDrag.value = dragBottomSheetHandler(panelEl, handleEl, {
			isTouchMode,
			dragSenderMode,
			dragSenderIsDragging,
			iframeDocument,
		});
	},
	{ flush: 'post' }
);

watch(
	() => dragSenderMode.value,
	(mode) => {
		if (mode === 'mini' && props.hideDetailOnMini && isTouchMode.value && detailMarkerVisible.value) {
			hideDetailMarker();
		}
	}
);

// #endregion

// #region Search

function buildSearchIndexFromPoints(points) {
	const features = (points || [])
		.map((point) => {
			const coordinates = Array.isArray(point) ? point : [Number(point.lng), Number(point.lat)];
			return {
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates,
				},
				properties: Array.isArray(point) ? {} : { ...point },
			};
		})
		.filter((feature) => {
			const [lng, lat] = feature.geometry.coordinates || [];
			return Number.isFinite(lng) && Number.isFinite(lat);
		});

	buildSearchIndex(features);
}

function buildSearchIndex(features) {
	searchIndex.value = features.map((f) => {
		let searchText = '';

		if (Array.isArray(props.searchIndexKeys) && props.searchIndexKeys.length > 0) {
			searchText = props.searchIndexKeys
				.map((key) => {
					const keys = key.split('.');
					let value = f.properties;

					keys.forEach((k) => {
						if (value && typeof value === 'object' && k in value) {
							value = value[k];
						} else {
							value = '';
						}
					});

					return value ? String(value).toLowerCase() : '';
				})
				.join(' ');
		} else {
			searchText = Object.values(f.properties || {})
				.filter((v) => v != null)
				.join(' ')
				.toLowerCase();
		}

		return {
			feature: f,
			searchText,
			name: f.properties.title || f.properties.label || f.properties.name || 'Unknown',
		};
	});
}

function onSearchClick() {
	emit('action-focus');
	hideDetailMarker();

	if (searchQuery.value.length > 0) {
		onSearchInput();
	}
}

function onSearchKeyDown(e) {
	if (!searchMenuOpen.value || searchResults.value.length === 0) return;

	switch (e.key) {
		case 'Enter':
			e.preventDefault();
			if (searchResults.value[selectedSearchIndex.value]) {
				selectSearchResult(searchResults.value[selectedSearchIndex.value]);
			}
			break;
		case 'ArrowDown':
			e.preventDefault();
			selectedSearchIndex.value = Math.min(selectedSearchIndex.value + 1, searchResults.value.length - 1);
			break;
		case 'ArrowUp':
			e.preventDefault();
			selectedSearchIndex.value = Math.max(selectedSearchIndex.value - 1, 0);
			break;
		case 'Escape':
			e.preventDefault();
			clearSearch();
			break;
	}
}

function onSearchInput() {
	const searchTerm = searchQuery.value.toLowerCase().trim();

	if (!searchTerm) {
		searchMenuOpen.value = false;
		searchResults.value = [];
		selectedSearchIndex.value = 0;
		return;
	}

	const results = searchIndex.value
		.filter((item) => {
			const itemText = item.searchText || '';
			return searchTerm.split(' ').every((term) => itemText.includes(term));
		})
		.slice(0, 10);

	searchResults.value = results;
	selectedSearchIndex.value = 0;
	searchMenuOpen.value = true;
}

function selectSearchResult(result) {
	if (!iframeMap || !result.feature) return;

	iframeMap.easeTo({
		center: result.feature.geometry.coordinates,
		zoom: 14,
	});

	searchMenuOpen.value = false;
	searchQuery.value = result.name;
	showDetailMarker(result.feature.properties);
}

function clearSearch() {
	searchQuery.value = '';
	searchResults.value = [];
	searchMenuOpen.value = false;
}

// #endregion

// #region Filters & Tools

function getToolState(toolId) {
	return toolBoxTools.value.find((t) => t.id === toolId) || null;
}

function applyFilters(locations = null) {
	const list = Array.isArray(locations) && locations.length > 0 ? locations : props.locations || [];
	return filtersComponentRef.value?.applyFilters?.(list);
}

function isFilterDirty() {
	return filterDirty.value;
}

// #endregion

// #region Locations adapter

function mapLocationToPoint(loc) {
	const id = loc?.[props.locationIdKey];
	const lng = Number(loc?._geo?.longitude || loc?.lng);
	const lat = Number(loc?._geo?.latitude || loc?.lat);
	const title = loc?.[props.locationTitleKey] ?? '';
	const icon = getDeliveryLocationTypeIcon(loc);
	const color = getDeliveryLocationTypeColor(loc);
	return { id, title, icon, color, ...loc, lat, lng };
}

function buildPointsFromLocations(locs) {
	return (locs || []).map(mapLocationToPoint).filter((point) => {
		return !isNaN(point.lng) && !isNaN(point.lat) && point.lng !== null && point.lat !== null && point.lng !== undefined && point.lat !== undefined;
	});
}

// #endregion

// #region Public API

function fitBounds(bounds, paddingParam) {
	if (!iframeMap) return;
	const padding = {
		padding: {
			top: 100,
			right: 50,
			bottom: 50,
			left: iframeDocument.querySelector('.marker-detail-container.visible') ? iframeDocument.querySelector('.marker-detail-container.visible').offsetWidth + 50 : 50,
		},
	};
	iframeMap.fitBounds(bounds, paddingParam || padding);
}

function setCenterZoom(center, zoom, options = {}) {
	if (!iframeMap) return;
	if (options.animate === false) {
		iframeMap.jumpTo({ center, zoom });
	} else {
		iframeMap.easeTo({ center, zoom });
	}
}

function setFilter(filterExpr) {
	if (!iframeMap) return;
}

function setLines(polylines) {
	if (!iframeMap) return;
	updateLines(polylines);
}

function setPolys(polygons) {
	if (!iframeMap) return;
	updatePolys(polygons);
}

function setHeat(heat) {
	if (!iframeMap) return;
	updateHeat(heat);
}

function resize() {
	if (iframeMap) {
		iframeMap.resize();
	}
}

function fitAll() {
	const allLocations = props.points?.length ? props.points : buildPointsFromLocations(props.locations);
	if (!allLocations.length || !iframeMap) return;

	const validLocations = allLocations.filter((location) => {
		const lng = Array.isArray(location) ? location[0] : location.lng || location._geo?.longitude;
		const lat = Array.isArray(location) ? location[1] : location.lat || location._geo?.latitude;
		return lng != null && lat != null;
	});

	if (!validLocations.length) return;

	const coordinates = validLocations.map((location) => {
		const lng = Array.isArray(location) ? location[0] : location.lng || location._geo?.longitude;
		const lat = Array.isArray(location) ? location[1] : location.lat || location._geo?.latitude;
		return [lng, lat];
	});
	const bounds = coordinates.reduce(
		(bounds, coord) => {
			return bounds.extend(coord);
		},
		new iframeLib.LngLatBounds(coordinates[0], coordinates[0])
	);

	fitBounds(bounds);
}

function fitCoordinates(coords) {
	if (!coords?.length || !iframeMap) return;
	const valid = coords.filter(([lng, lat]) => lng != null && lat != null);
	if (!valid.length) return;
	const bounds = valid.reduce((b, coord) => b.extend(coord), new iframeLib.LngLatBounds(valid[0], valid[0]));
	fitBounds(bounds);
}

function fitAllWithUserPosition() {
	const points = props.points?.length ? props.points.slice() : buildPointsFromLocations(props.locations);

	const userPos = userCurrentPosition.value;
	if (userPos) {
		points.push([userPos.lng, userPos.lat]);
	}

	if (!points.length || !iframeMap) return;

	const validLocations = points.filter((location) => {
		const lng = Array.isArray(location) ? location[0] : location.lng || location._geo?.longitude;
		const lat = Array.isArray(location) ? location[1] : location.lat || location._geo?.latitude;
		return lng != null && lat != null;
	});

	if (!validLocations.length) return;

	const coordinates = validLocations.map((location) => {
		const lng = Array.isArray(location) ? location[0] : location.lng || location._geo?.longitude;
		const lat = Array.isArray(location) ? location[1] : location.lat || location._geo?.latitude;
		return [lng, lat];
	});

	const bounds = coordinates.reduce((bounds, coord) => bounds.extend(coord), new iframeLib.LngLatBounds(coordinates[0], coordinates[0]));

	fitBounds(bounds);
}

// #endregion

// #region Reactivity

watch(
	() => props.points,
	(v) => {
		if (!iframeMap || !isLoaded) return;
		if (clusterEnabled.value) {
			updatePoints(v);
		} else {
			addHtmlMarkers(v?.length ? v : buildPointsFromLocations(props.locations));
		}
	},
	{ deep: true }
);

watch(
	() => props.polylines,
	(v) => {
		if (!iframeMap || !isLoaded) return;
		updateLines(v);
	},
	{ deep: true }
);

watch(
	() => props.polygons,
	(v) => {
		if (!iframeMap || !isLoaded) return;
		updatePolys(v);
	},
	{ deep: true }
);

watch(
	() => props.heatmap,
	(v) => {
		if (!iframeMap || !isLoaded) return;
		updateHeat(v);
	},
	{ deep: true }
);

watch(currentRoute, (route) => {
	if (!route) {
		emit('route-summary-changed', null);
		return;
	}
	emit('route-summary-changed', { duration: translateDuration(route.durationText), distance: translateDistance(route.distanceText) });
});

watch(
	() => props.fullscreen,
	(v) => {
		if (!root.value) return;
		nextTick(() => {
			if (v) {
				root.value.classList.add('is-fullscreen');
			} else {
				root.value.classList.remove('is-fullscreen');
			}
			if (iframeMap) iframeMap.resize();
		});
	}
);

watch(
	() => props.locations,
	(v) => {
		if (!iframeMap || !isLoaded) return;
		const points = props.points?.length ? props.points : buildPointsFromLocations(v);
		if (clusterEnabled.value) {
			updatePoints(points);
		} else {
			addHtmlMarkers(points);
		}
	},
	{ deep: true }
);

watch(
	() => props.routeGeoJson,
	(v) => {
		if (!iframeMap || !isLoaded) return;
		showDirections(v);
	},
	{ deep: true }
);

watch(
	() => props.toolBox,
	(v) => {
		if (!iframeMap || !isLoaded) return;
		showDirections(v);
	},
	{ deep: true }
);

function updatePoints(points) {
	clusterModeActive = true;
	const geojson = buildGeoJson(points?.length ? points : buildPointsFromLocations(props.locations));

	if (!iframeMap.getSource('points')) {
		addGeoJsonSource('points', {
			type: 'geojson',
			data: geojson,
			cluster: clusterEnabled.value,
			clusterMaxZoom: props.clusterMaxZoom,
			clusterRadius: props.clusterRadius,
		});

		addClusterLayers();
		registerClusterClick();
		registerClusterHover();
		registerMapInteractions();
	} else {
		setSourceData('points', geojson);
	}
}

function updateLines(lines) {
	const data = buildLines(lines);
	if (!iframeMap.getSource('lines')) {
		addGeoJsonSource('lines', { type: 'geojson', data });
		addPolylineLayers();
	} else {
		setSourceData('lines', data);
	}
}

function updatePolys(polys) {
	const data = buildPolygons(polys);
	if (!iframeMap.getSource('polys')) {
		addGeoJsonSource('polys', { type: 'geojson', data });
		addPolygonLayers();
	} else {
		setSourceData('polys', data);
	}
}

function updateHeat(heat) {
	const data = buildHeat(heat);
	if (!iframeMap.getSource('heat')) {
		addGeoJsonSource('heat', { type: 'geojson', data });
		addHeatmapLayer();
	} else {
		setSourceData('heat', data);
	}
}

// #endregion

// #region Legacy public API (locations)

function setLocations(locs) {
	if (!iframeMap) return;
	if (clusterEnabled.value) {
		const pts = buildPointsFromLocations(locs || []);
		updatePoints(pts);
	} else {
		addHtmlMarkers(buildPointsFromLocations(locs || []));
	}
}

function addLocations(locs) {
	const merged = (props.locations || []).concat(locs || []);
	setLocations(merged);
}

function clearLocations() {
	setLocations([]);
}

function focusLocation(id, zoom = 14) {
	if (!iframeMap) return;
	const all = props.points?.length ? props.points : buildPointsFromLocations(props.locations);
	const found = all.find((p) => String(p.id) === String(id));
	if (!found) return;
	iframeMap.easeTo({ center: [found.lng, found.lat], zoom });
}

function selectLocation(id, withPopup = true) {
	if (!iframeMap) return;
	const all = props.points?.length ? props.points : buildPointsFromLocations(props.locations);
	const found = all.find((p) => String(p.id) === String(id));
	if (!found) return;
	const feature = {
		geometry: { coordinates: [found.lng, found.lat] },
		properties: found,
	};
	if (withPopup) {
		showPopup(feature);
	}
	iframeMap.easeTo({ center: [found.lng, found.lat] });
}

// #endregion

// #region Offline mode

function startZoneSelection() {
	offlineZoneSelecting.value = true;
	offlinePanelVisible.value = false;
}

function confirmZoneSelection() {
	const bounds = getZoneLngLat();
	if (bounds) {
		offlineZoneBounds.value = bounds;
		downloadOfflineRegion(bounds);
	}
	offlineZoneSelecting.value = false;
}

function cancelZoneSelection() {
	offlineZoneSelecting.value = false;
}

function getZoneLngLat() {
	if (!iframeMap) return null;
	const b = iframeMap.getBounds();
	return { north: b.getNorth(), south: b.getSouth(), west: b.getWest(), east: b.getEast() };
}

async function downloadOfflineRegion(bounds) {
	if (!offlineControl || !styleUrl) return;

	toast?.show?.({ message: t('OfflineDownloadStarted'), type: 'info' });

	try {
		await offlineControl.downloadManager.downloadRegion({
			name: 'Offline Zone',
			bounds: [bounds.west, bounds.south, bounds.east, bounds.north],
			minZoom: props.minZoom,
			maxZoom: Math.min(props.maxZoom, 14),
			styleUrl,
			provider: 'maplibre',
		});

		const analytics = await offlineManager.getComprehensiveStorageAnalytics();
		offlineTilesSize.value = analytics.totalStorageSize ?? null;
		offlinePanelVisible.value = true;
		toast?.show?.({ message: t('OfflineDownloadComplete'), type: 'success' });
	} catch {
		toast?.show?.({ message: t('OfflineDownloadError'), type: 'error' });
	}
}

// #endregion
</script>

<style>
.ml-container .marker {
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	user-select: none;
}

.ml-container .marker img {
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	user-select: none;
}
</style>
